'use client'

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react'
// Using shared audio context
import { getAudioContext } from '../../lib/audio/audioContext'
import { CHART_COLORS } from '../charts/chart-config'
import { logger } from '@/lib/logger'

interface SWSNeuralSynthesizerProps {
  className?: string
}

interface SynthesisParameters {
  algorithm: 'cassiopeia_fusion' | 'mystic_acid' | 'neural_evolution' | 'quantum_harmonics' | 'transformer_ai' | 'quantum_entanglement'
  complexity: number // 0-1
  evolution: number // 0-1
  resonance: number // 0-1
  intensity: number // 0-1
  // Enhanced 8-band neural parameters
  drive: number // 0-1 - Neural network intensity
  diffusion: number // 0-1 - Spatial processing
  uncertainty: number // 0-1 - Algorithmic randomness
  // Quantum-inspired parameters
  entanglement: number // 0-1 - Quantum entanglement strength
  superposition: number // 0-1 - Quantum superposition probability
  quantum_tunneling: number // 0-1 - Quantum tunneling effects
  wave_function: number // 0-1 - Wave function modulation
  // Spatial audio parameters
  azimuth: number // -π to π - Horizontal angle
  elevation: number // -π/2 to π/2 - Vertical angle
  distance: number // 0.1 to 10 - Distance from listener
  // Attention mechanism parameters
  attention_heads: number // 1-8 - Number of attention heads
  attention_span: number // 32-256 - Attention memory span
  memory_decay: number // 0.8-0.99 - Memory decay rate
}

interface NeuralLayer {
  weights: number[]
  biases: number[]
  activation: 'tanh' | 'relu' | 'sigmoid'
}

class SWSNeuralEngine {
  private audioContext: AudioContext
  private layers: NeuralLayer[] = []
  private evolutionBuffer: Float32Array
  private workletNode?: AudioWorkletNode

  constructor(audioContext: AudioContext) {
    this.audioContext = audioContext
    this.evolutionBuffer = new Float32Array(1024)
    this.initializeNeuralNetwork()
  }

  private initializeNeuralNetwork() {
    // Cassiopeia-inspired neural architecture
    this.layers = [
      { weights: this.generateWeights(8, 16), biases: this.generateBiases(16), activation: 'tanh' },
      { weights: this.generateWeights(16, 32), biases: this.generateBiases(32), activation: 'relu' },
      { weights: this.generateWeights(32, 64), biases: this.generateBiases(64), activation: 'relu' },
      { weights: this.generateWeights(64, 32), biases: this.generateBiases(32), activation: 'tanh' },
      { weights: this.generateWeights(32, 16), biases: this.generateBiases(16), activation: 'sigmoid' },
      { weights: this.generateWeights(16, 8), biases: this.generateBiases(8), activation: 'tanh' }
    ]
  }

  private generateWeights(inputSize: number, outputSize: number): number[] {
    const weights: number[] = []
    for (let i = 0; i < inputSize * outputSize; i++) {
      weights.push((Math.random() - 0.5) * 0.1)
    }
    return weights
  }

  private generateBiases(size: number): number[] {
    return Array.from({ length: size }, () => (Math.random() - 0.5) * 0.01)
  }

  private activate(value: number, activation: string): number {
    switch (activation) {
      case 'tanh': return Math.tanh(value)
      case 'relu': return Math.max(0, value)
      case 'sigmoid': return 1 / (1 + Math.exp(-value))
      default: return value
    }
  }

  public processNeuralSignal(input: Float32Array, parameters: SynthesisParameters): Float32Array {
    let signal = Array.from(input)

    // Enhanced neural processing with 5-band parameters
    for (const layer of this.layers) {
      const newSignal: number[] = []

      for (let i = 0; i < layer.biases.length; i++) {
        let sum = layer.biases[i] * parameters.drive // Neural drive amplification

        for (let j = 0; j < signal.length; j++) {
          // Add uncertainty-based weight modulation
          const weightMod = 1 + (Math.random() - 0.5) * parameters.uncertainty
          sum += signal[j] * layer.weights[j * layer.biases.length + i] * weightMod
        }

        newSignal.push(this.activate(sum, layer.activation))
      }

      signal = newSignal
    }

    // Apply advanced synthesis parameters with Cassiopeia fusion
    const output = new Float32Array(input.length)
    for (let i = 0; i < input.length; i++) {
      const evolutionIndex = Math.floor((i / input.length) * signal.length)
      const evolutionFactor = signal[evolutionIndex] || 0

      // Base signal with neural drive
      const baseSignal = input[i] * (1 + parameters.drive)

      // Cassiopeia fusion harmonics with uncertainty
      const uncertaintyMod = 1 + (Math.random() - 0.5) * parameters.uncertainty
      const harmonic1 = Math.sin(2 * Math.PI * evolutionFactor * 2 * uncertaintyMod) * parameters.resonance
      const harmonic2 = Math.sin(2 * Math.PI * evolutionFactor * 3 * uncertaintyMod) * parameters.intensity
      const harmonic3 = Math.sin(2 * Math.PI * evolutionFactor * 5 * uncertaintyMod) * parameters.complexity

      // Diffusion processing (spatial harmonics)
      const diffusionFactor = parameters.diffusion * Math.sin(2 * Math.PI * evolutionFactor * 7)
      const diffusedSignal = baseSignal + harmonic1 + harmonic2 + harmonic3

      // Final evolution and diffusion mix
      output[i] = diffusedSignal * (1 + evolutionFactor * parameters.evolution) * (1 + diffusionFactor)
    }

    return output
  }

  public async generateMysticFusion(duration: number, parameters: SynthesisParameters): Promise<AudioBuffer> {
    const sampleRate = this.audioContext.sampleRate
    const numSamples = Math.floor(sampleRate * duration)
    const buffer = this.audioContext.createBuffer(2, numSamples, sampleRate) // Stereo

    // Generate enhanced base signal with Cassiopeia fusion algorithms
    const leftChannel = new Float32Array(numSamples)
    const rightChannel = new Float32Array(numSamples)

    for (let i = 0; i < numSamples; i++) {
      const t = i / sampleRate
      const evolution = parameters.evolution * Math.sin(2 * Math.PI * t * 0.5)

      // Advanced Cassiopeia fusion algorithm with 5-band neural parameters
      const baseFreq = 220 + evolution * 110 * parameters.drive

      // Multi-octave Cassiopeia scale with uncertainty modulation
      const scalePosition = Math.floor((t % 1) * 7)
      const scaleMultipliers = [1, 9/8, 5/4, 4/3, 3/2, 5/3, 15/8]
      const uncertaintyMod = 1 + (Math.random() - 0.5) * parameters.uncertainty * 0.1

      const scaledFreq = baseFreq * scaleMultipliers[scalePosition] * uncertaintyMod

      // Neural drive-enhanced oscillators
      const driveAmp = 1 + parameters.drive * 2
      const left = Math.sin(2 * Math.PI * scaledFreq * t) * driveAmp * (1 - evolution * 0.5)
      const right = Math.sin(2 * Math.PI * (scaledFreq * 1.01) * t) * driveAmp * (1 - evolution * 0.3)

      // Add diffusion-based stereo enhancement
      const diffusionPhase = parameters.diffusion * Math.sin(2 * Math.PI * t * 2)
      const diffusedLeft = left * (1 + diffusionPhase * 0.3)
      const diffusedRight = right * (1 - diffusionPhase * 0.3)

      leftChannel[i] = diffusedLeft
      rightChannel[i] = diffusedRight
    }

    // Apply enhanced neural processing with all 5-band parameters
    const processedLeft = this.processNeuralSignal(leftChannel, parameters)
    const processedRight = this.processNeuralSignal(rightChannel, parameters)

    // Apply final diffusion post-processing
    this.applyDiffusionPostProcessing(processedLeft, processedRight, parameters)

    // Ensure proper Float32Array types for AudioBuffer
    buffer.copyFromChannel(new Float32Array(processedLeft), 0)
    buffer.copyFromChannel(new Float32Array(processedRight), 1)

    return buffer as AudioBuffer
  }

  private applyDiffusionPostProcessing(
    leftChannel: Float32Array,
    rightChannel: Float32Array,
    parameters: SynthesisParameters
  ): void {
    const diffusionStrength = parameters.diffusion
    const delaySamples = Math.floor(this.audioContext.sampleRate * 0.02) // 20ms delay

    for (let i = delaySamples; i < leftChannel.length; i++) {
      const diffusionAmount = diffusionStrength * 0.3
      leftChannel[i] += rightChannel[i - delaySamples] * diffusionAmount
      rightChannel[i] += leftChannel[i - delaySamples] * diffusionAmount
    }
  }

  public evolveParameters(baseParams: SynthesisParameters): SynthesisParameters {
    // Enhanced genetic algorithm for parameter evolution with neural parameters
    const mutationRate = 0.1
    const mutationStrength = 0.2

    return {
      algorithm: baseParams.algorithm,
      complexity: Math.max(0, Math.min(1, baseParams.complexity + (Math.random() - 0.5) * mutationStrength)),
      evolution: Math.max(0, Math.min(1, baseParams.evolution + (Math.random() - 0.5) * mutationStrength)),
      resonance: Math.max(0, Math.min(1, baseParams.resonance + (Math.random() - 0.5) * mutationStrength)),
      intensity: Math.max(0, Math.min(1, baseParams.intensity + (Math.random() - 0.5) * mutationStrength)),
      // Evolve neural parameters with genetic algorithm
      drive: Math.max(0, Math.min(1, baseParams.drive + (Math.random() - 0.5) * mutationStrength)),
      diffusion: Math.max(0, Math.min(1, baseParams.diffusion + (Math.random() - 0.5) * mutationStrength)),
      uncertainty: Math.max(0, Math.min(1, baseParams.uncertainty + (Math.random() - 0.5) * mutationStrength)),
      // Evolve quantum parameters
      entanglement: Math.max(0, Math.min(1, baseParams.entanglement + (Math.random() - 0.5) * mutationStrength)),
      superposition: Math.max(0, Math.min(1, baseParams.superposition + (Math.random() - 0.5) * mutationStrength)),
      quantum_tunneling: Math.max(0, Math.min(1, baseParams.quantum_tunneling + (Math.random() - 0.5) * mutationStrength)),
      wave_function: Math.max(0, Math.min(1, baseParams.wave_function + (Math.random() - 0.5) * mutationStrength)),
      // Evolve spatial parameters
      azimuth: baseParams.azimuth + (Math.random() - 0.5) * 0.1,
      elevation: Math.max(-Math.PI/2, Math.min(Math.PI/2, baseParams.elevation + (Math.random() - 0.5) * 0.1)),
      distance: Math.max(0.1, Math.min(10, baseParams.distance + (Math.random() - 0.5) * 0.5)),
      // Evolve attention parameters
      attention_heads: Math.max(1, Math.min(8, Math.round(baseParams.attention_heads + (Math.random() - 0.5) * 2))),
      attention_span: Math.max(32, Math.min(256, Math.round(baseParams.attention_span + (Math.random() - 0.5) * 32))),
      memory_decay: Math.max(0.8, Math.min(0.99, baseParams.memory_decay + (Math.random() - 0.5) * 0.05))
    }
  }
}

// ============================================================================
// PERFORMANCE OPTIMIZED NEURAL SYNTHESIZER
// ============================================================================

const SWSNeuralSynthesizer: React.FC<SWSNeuralSynthesizerProps> = React.memo(({ className = '' }) => {
  // Refs for audio components - optimized with proper cleanup
  const audioContextRef = useRef<AudioContext | null>(null)
  const neuralEngineRef = useRef<SWSNeuralEngine | null>(null)
  const audioSourceRef = useRef<AudioBufferSourceNode | null>(null)
  const animationFrameRef = useRef<number | null>(null)
  const performanceMetricsRef = useRef({
    generationTime: 0,
    playbackStartTime: 0,
    lastUpdateTime: Date.now()
  })

  // Memoized default parameters to prevent unnecessary re-renders
  const defaultParameters = useMemo<SynthesisParameters>(() => ({
    algorithm: 'cassiopeia_fusion',
    complexity: 0.7,
    evolution: 0.5,
    resonance: 0.8,
    intensity: 0.6,
    drive: 0.8,
    diffusion: 0.4,
    uncertainty: 0.3,
    // Quantum parameters
    entanglement: 0.5,
    superposition: 0.3,
    quantum_tunneling: 0.2,
    wave_function: 0.4,
    // Spatial parameters
    azimuth: 0,
    elevation: 0,
    distance: 1.0,
    // Attention parameters
    attention_heads: 4,
    attention_span: 128,
    memory_decay: 0.9
  }), [])

  // State with optimized updates
  const [parameters, setParameters] = useState<SynthesisParameters>(defaultParameters)
  const [isGenerating, setIsGenerating] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentBuffer, setCurrentBuffer] = useState<AudioBuffer | null>(null)
  const [evolutionHistory, setEvolutionHistory] = useState<SynthesisParameters[]>([])
  const [audioWorkletSupported, setAudioWorkletSupported] = useState(false)
  const [performanceMetrics, setPerformanceMetrics] = useState({
    averageGenerationTime: 0,
    lastGenerationTime: 0,
    totalGenerations: 0
  })

  // Optimized initialization with shared audio context
  const initializeSWS = useCallback(async () => {
    if (!audioContextRef.current) {
      try {
        audioContextRef.current = await getAudioContext()
        logger.log('Shared AudioContext obtained:', audioContextRef.current.sampleRate + 'Hz')
      } catch (error) {
        logger.error('Failed to get shared AudioContext:', error)
        return
      }
    }

    if (!neuralEngineRef.current) {
      neuralEngineRef.current = new SWSNeuralEngine(audioContextRef.current)
      logger.log('Neural engine initialized')
    }

    // Initialize AudioWorklet for real-time processing with enhanced fallback
    let audioWorkletSupported = false
    try {
      if (audioContextRef.current && audioContextRef.current.audioWorklet) {
        // Check if AudioWorklet is actually supported before trying to load
        if (typeof AudioWorkletNode !== 'undefined') {
          await audioContextRef.current.audioWorklet.addModule('/audio-worklets/sws-neural-processor.js')
          logger.log('AudioWorklet loaded successfully')
          audioWorkletSupported = true
        } else {
          logger.warn('AudioWorklet not supported in this browser environment')
        }
      } else {
        logger.warn('AudioWorklet not available in AudioContext')
      }
    } catch (error) {
      logger.warn('AudioWorklet loading failed, using fallback processing:', error)

      // Provide more specific error information
      if (error instanceof Error) {
        if (error.message.includes('404') || error.message.includes('Not Found')) {
          logger.error('AudioWorklet module not found at /audio-worklets/sws-neural-processor.js')
        } else if (error.message.includes('network') || error.message.includes('fetch')) {
          logger.error('Network error loading AudioWorklet module')
        } else {
          logger.error('AudioWorklet error:', error.message)
        }
      }
    }

    // Store AudioWorklet support status for fallback logic
    setAudioWorkletSupported(audioWorkletSupported)
  }, [])

  // Performance-monitored audio generation
  const generateNeuralAudio = useCallback(async () => {
    if (!neuralEngineRef.current || !audioContextRef.current) {
      logger.warn('Audio components not initialized')
      return
    }

    const startTime = performance.now()
    setIsGenerating(true)

    try {
      const buffer = await neuralEngineRef.current.generateMysticFusion(4.0, parameters)
      const generationTime = performance.now() - startTime

      setCurrentBuffer(buffer)
      setEvolutionHistory(prev => [...prev.slice(-4), parameters])

      // Update performance metrics
      setPerformanceMetrics(prev => ({
        averageGenerationTime: (prev.averageGenerationTime * prev.totalGenerations + generationTime) / (prev.totalGenerations + 1),
        lastGenerationTime: generationTime,
        totalGenerations: prev.totalGenerations + 1
      }))

      logger.log(`Audio generated in ${generationTime.toFixed(2)}ms`)
    } catch (error) {
      logger.error('SWS Neural Generation Error:', error)
      // Could implement retry logic here
    } finally {
      setIsGenerating(false)
    }
  }, [parameters])

  // Optimized parameter evolution with debouncing
  const evolveSynthesis = useCallback(() => {
    if (!neuralEngineRef.current) {
      logger.warn('Neural engine not available for evolution')
      return
    }

    const evolvedParams = neuralEngineRef.current.evolveParameters(parameters)
    setParameters(evolvedParams)
    logger.log('Parameters evolved:', evolvedParams)
  }, [parameters])

  // Optimized playback with proper cleanup and error handling
  const playSWS = useCallback(async () => {
    if (!currentBuffer || !audioContextRef.current) {
      logger.warn('No audio buffer or context available for playback')
      return
    }

    try {
      // Resume context if suspended
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume()
      }

      // Clean up previous source
      if (audioSourceRef.current) {
        try {
          audioSourceRef.current.stop()
        } catch (error) {
          // Source might already be stopped, ignore
        }
        audioSourceRef.current = null
      }

      // Create and configure new source
      const source = audioContextRef.current.createBufferSource()
      source.buffer = currentBuffer
      source.connect(audioContextRef.current.destination)

      // Performance monitoring
      performanceMetricsRef.current.playbackStartTime = performance.now()

      source.start()
      audioSourceRef.current = source as unknown as AudioBufferSourceNode
      setIsPlaying(true)

      source.onended = () => {
        const playbackTime = performance.now() - performanceMetricsRef.current.playbackStartTime
        logger.log(`Playback completed in ${playbackTime.toFixed(2)}ms`)
        setIsPlaying(false)
        audioSourceRef.current = null
      }

    } catch (error) {
      logger.error('SWS Playback Error:', error)
      setIsPlaying(false)
    }
  }, [currentBuffer])

  // Enhanced stop function with cleanup
  const stopSWS = useCallback(() => {
    if (audioSourceRef.current) {
      try {
        audioSourceRef.current.stop()
      } catch (error) {
        // Source might already be stopped, ignore
      }
      audioSourceRef.current = null
    }
    setIsPlaying(false)
  }, [])

  // Optimized initialization effect
  useEffect(() => {
    initializeSWS()
  }, [initializeSWS])

  // Cleanup effect for proper resource management
  useEffect(() => {
    return () => {
      // Clean up audio resources
      if (audioSourceRef.current) {
        try {
          audioSourceRef.current.stop()
        } catch (error) {
          // Ignore cleanup errors
        }
        audioSourceRef.current = null
      }

      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        try {
          audioContextRef.current.close()
        } catch (error) {
          // Ignore cleanup errors
        }
      }

      // Cancel any pending animation frames
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }

      logger.log('SWS Neural Synthesizer cleaned up')
    }
  }, [])

  // Performance monitoring effect
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now()
      const timeSinceLastUpdate = now - performanceMetricsRef.current.lastUpdateTime

      if (timeSinceLastUpdate > 5000) { // Log every 5 seconds
        logger.log('SWS Performance:', {
          isPlaying,
          isGenerating,
          bufferSize: currentBuffer?.length,
          sampleRate: audioContextRef.current?.sampleRate,
          avgGenerationTime: performanceMetrics.averageGenerationTime.toFixed(2) + 'ms',
          totalGenerations: performanceMetrics.totalGenerations
        })
        performanceMetricsRef.current.lastUpdateTime = now
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [isPlaying, isGenerating, currentBuffer, performanceMetrics])

  const algorithms = [
    { id: 'cassiopeia_fusion', name: 'Cassiopeia Fusion', desc: 'Japanese precision harmonics' },
    { id: 'mystic_acid', name: 'Mystic Acid', desc: 'Psychedelic neural waves' },
    { id: 'neural_evolution', name: 'Neural Evolution', desc: 'AI-driven adaptation' },
    { id: 'quantum_harmonics', name: 'Quantum Harmonics', desc: 'Uncertainty principle audio' },
    { id: 'transformer_ai', name: 'Transformer AI', desc: 'Attention-based synthesis' },
    { id: 'quantum_entanglement', name: 'Quantum Entanglement', desc: 'Multi-dimensional harmonics' }
  ]

  return (
    <div className={`sws-neural-synthesizer ${className} overflow-visible`}>
      <div className="bg-black/20 backdrop-blur-sm rounded-xl p-6 border border-cyan-500/30 min-h-0 overflow-visible">

        {/* Algorithm Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-cyan-300 mb-2">Synthesis Algorithm</label>
          <div className="grid grid-cols-2 gap-3">
            {algorithms.map((algo) => (
              <button
                key={algo.id}
                onClick={() => setParameters(prev => ({ ...prev, algorithm: algo.id as any }))}
                className={`p-3 rounded-lg border transition-all ${
                  parameters.algorithm === algo.id
                    ? 'border-cyan-400 bg-cyan-400/20 text-cyan-300'
                    : 'border-cyan-600/30 bg-black/50 text-cyan-400/70 hover:border-cyan-500/50'
                }`}
              >
                <div className="font-medium">{algo.name}</div>
                <div className="text-xs opacity-70">{algo.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Core Neural Parameters */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          {[
            { key: 'complexity', label: 'Complexity', color: 'cyan' },
            { key: 'evolution', label: 'Evolution', color: 'purple' },
            { key: 'resonance', label: 'Resonance', color: 'green' },
            { key: 'intensity', label: 'Intensity', color: 'red' },
            { key: 'drive', label: 'Neural Drive', color: 'orange' },
            { key: 'diffusion', label: 'Diffusion', color: 'pink' },
            { key: 'uncertainty', label: 'Uncertainty', color: 'indigo' }
          ].map(({ key, label, color }) => (
            <div key={key}>
              <label className="block text-sm font-medium text-cyan-300 mb-1">{label}</label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={parameters[key as keyof SynthesisParameters] as number}
                onChange={(e) => setParameters(prev => ({
                  ...prev,
                  [key]: parseFloat(e.target.value)
                }))}
                className={`w-full h-2 rounded-lg appearance-none cursor-pointer bg-${color}-900`}
                style={{
                  background: `linear-gradient(to right, #${
                    color === 'cyan' ? '06b6d4' :
                    color === 'purple' ? 'a855f7' :
                    color === 'green' ? '22c55e' :
                    color === 'red' ? 'ef4444' :
                    color === 'orange' ? 'f97316' :
                    color === 'pink' ? 'ec4899' :
                    '6366f1'
                  } ${(parameters[key as keyof SynthesisParameters] as number) * 100}%, #374151 ${(parameters[key as keyof SynthesisParameters] as number) * 100}%)`
                }}
                aria-label={label}
                title={label}
              />
              <div className="text-xs text-cyan-400/60 mt-1">
                {((parameters[key as keyof SynthesisParameters] as number) * 100).toFixed(0)}%
              </div>
            </div>
          ))}
        </div>

        {/* Quantum Parameters */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-purple-300 mb-3">⚛️ Quantum Parameters</h3>
          <div className="grid grid-cols-2 gap-4">
            {[
              { key: 'entanglement', label: 'Entanglement', color: 'purple' },
              { key: 'superposition', label: 'Superposition', color: 'violet' },
              { key: 'quantum_tunneling', label: 'Quantum Tunneling', color: 'fuchsia' },
              { key: 'wave_function', label: 'Wave Function', color: 'pink' }
            ].map(({ key, label, color }) => (
              <div key={key}>
                <label className="block text-sm font-medium text-purple-300 mb-1">{label}</label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={parameters[key as keyof SynthesisParameters] as number}
                  onChange={(e) => setParameters(prev => ({
                    ...prev,
                    [key]: parseFloat(e.target.value)
                  }))}
                  className={`w-full h-2 rounded-lg appearance-none cursor-pointer bg-${color}-900`}
                  style={{
                    background: `linear-gradient(to right, #${
                      color === 'purple' ? 'a855f7' :
                      color === 'violet' ? '8b5cf6' :
                      color === 'fuchsia' ? 'd946ef' :
                      'ec4899'
                    } ${(parameters[key as keyof SynthesisParameters] as number) * 100}%, #374151 ${(parameters[key as keyof SynthesisParameters] as number) * 100}%)`
                  }}
                  aria-label={label}
                  title={label}
                />
                <div className="text-xs text-purple-400/60 mt-1">
                  {((parameters[key as keyof SynthesisParameters] as number) * 100).toFixed(0)}%
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Spatial Audio Parameters */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-green-300 mb-3">🌌 Spatial Audio</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-green-300 mb-1">Azimuth (-180° to 180°)</label>
              <input
                type="range"
                min="-3.14159"
                max="3.14159"
                step="0.01"
                value={parameters.azimuth}
                onChange={(e) => setParameters(prev => ({
                  ...prev,
                  azimuth: parseFloat(e.target.value)
                }))}
                className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-green-900"
                style={{
                  background: `linear-gradient(to right, #22c55e ${(parameters.azimuth + 3.14159) / (2 * 3.14159) * 100}%, #374151 ${(parameters.azimuth + 3.14159) / (2 * 3.14159) * 100}%)`
                }}
                aria-label="Azimuth"
                title="Azimuth"
              />
              <div className="text-xs text-green-400/60 mt-1">
                {(parameters.azimuth * 180 / Math.PI).toFixed(0)}°
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-green-300 mb-1">Elevation (-90° to 90°)</label>
              <input
                type="range"
                min="-1.5708"
                max="1.5708"
                step="0.01"
                value={parameters.elevation}
                onChange={(e) => setParameters(prev => ({
                  ...prev,
                  elevation: parseFloat(e.target.value)
                }))}
                className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-green-900"
                style={{
                  background: `linear-gradient(to right, #22c55e ${(parameters.elevation + 1.5708) / (2 * 1.5708) * 100}%, #374151 ${(parameters.elevation + 1.5708) / (2 * 1.5708) * 100}%)`
                }}
                aria-label="Elevation"
                title="Elevation"
              />
              <div className="text-xs text-green-400/60 mt-1">
                {(parameters.elevation * 180 / Math.PI).toFixed(0)}°
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-green-300 mb-1">Distance (0.1 to 10)</label>
              <input
                type="range"
                min="0.1"
                max="10"
                step="0.1"
                value={parameters.distance}
                onChange={(e) => setParameters(prev => ({
                  ...prev,
                  distance: parseFloat(e.target.value)
                }))}
                className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-green-900"
                style={{
                  background: `linear-gradient(to right, #22c55e ${(parameters.distance - 0.1) / 9.9 * 100}%, #374151 ${(parameters.distance - 0.1) / 9.9 * 100}%)`
                }}
                aria-label="Distance"
                title="Distance"
              />
              <div className="text-xs text-green-400/60 mt-1">
                {parameters.distance.toFixed(1)} units
              </div>
            </div>
          </div>
        </div>

        {/* Attention Mechanism Parameters */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-orange-300 mb-3">🧠 Attention Control</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-orange-300 mb-1">Attention Heads (1-8)</label>
              <input
                type="range"
                min="1"
                max="8"
                step="1"
                value={parameters.attention_heads}
                onChange={(e) => setParameters(prev => ({
                  ...prev,
                  attention_heads: parseInt(e.target.value)
                }))}
                className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-orange-900"
                style={{
                  background: `linear-gradient(to right, #f97316 ${(parameters.attention_heads - 1) / 7 * 100}%, #374151 ${(parameters.attention_heads - 1) / 7 * 100}%)`
                }}
                aria-label="Attention Heads"
                title="Attention Heads"
              />
              <div className="text-xs text-orange-400/60 mt-1">
                {parameters.attention_heads} heads
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-orange-300 mb-1">Attention Span (32-256)</label>
              <input
                type="range"
                min="32"
                max="256"
                step="8"
                value={parameters.attention_span}
                onChange={(e) => setParameters(prev => ({
                  ...prev,
                  attention_span: parseInt(e.target.value)
                }))}
                className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-orange-900"
                style={{
                  background: `linear-gradient(to right, #f97316 ${(parameters.attention_span - 32) / 224 * 100}%, #374151 ${(parameters.attention_span - 32) / 224 * 100}%)`
                }}
                aria-label="Attention Span"
                title="Attention Span"
              />
              <div className="text-xs text-orange-400/60 mt-1">
                {parameters.attention_span} samples
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-orange-300 mb-1">Memory Decay (0.8-0.99)</label>
              <input
                type="range"
                min="0.8"
                max="0.99"
                step="0.01"
                value={parameters.memory_decay}
                onChange={(e) => setParameters(prev => ({
                  ...prev,
                  memory_decay: parseFloat(e.target.value)
                }))}
                className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-orange-900"
                style={{
                  background: `linear-gradient(to right, #f97316 ${(parameters.memory_decay - 0.8) / 0.19 * 100}%, #374151 ${(parameters.memory_decay - 0.8) / 0.19 * 100}%)`
                }}
                aria-label="Memory Decay"
                title="Memory Decay"
              />
              <div className="text-xs text-orange-400/60 mt-1">
                {(parameters.memory_decay * 100).toFixed(1)}%
              </div>
            </div>
          </div>
        </div>

        {/* Control Panel */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex space-x-3">
            <button
              onClick={generateNeuralAudio}
              disabled={isGenerating}
              className="px-6 py-3 bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-500 hover:to-purple-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg font-medium text-white transition-all transform hover:scale-105"
            >
              {isGenerating ? '⚡ Generating...' : '🎵 Generate'}
            </button>

            <button
              onClick={evolveSynthesis}
              className="px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 rounded-lg font-medium text-white transition-all transform hover:scale-105"
            >
              🧬 Evolve
            </button>
          </div>

          <div className="flex space-x-3">
            <button
              onClick={playSWS}
              disabled={!currentBuffer || isPlaying}
              className="px-6 py-3 bg-gradient-to-r from-green-600 to-cyan-600 hover:from-green-500 hover:to-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg font-medium text-white transition-all transform hover:scale-105"
            >
              {isPlaying ? '⏸️ Playing' : '▶️ Play'}
            </button>

            <button
              onClick={stopSWS}
              disabled={!isPlaying}
              className="px-4 py-3 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-500 hover:to-orange-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg font-medium text-white transition-all transform hover:scale-105"
            >
              ⏹️ Stop
            </button>
          </div>
        </div>

        {/* Evolution History */}
        {evolutionHistory.length > 0 && (
          <div className="mb-4">
            <h3 className="text-lg font-medium text-cyan-300 mb-2">Evolution History</h3>
            <div className="flex space-x-2 overflow-x-auto">
              {evolutionHistory.map((params, index) => (
                <button
                  key={index}
                  onClick={() => setParameters(params)}
                  className="flex-shrink-0 px-3 py-2 bg-cyan-900/30 border border-cyan-500/30 rounded text-sm text-cyan-300 hover:bg-cyan-800/50 transition-colors"
                >
                  Gen {index + 1}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Performance Metrics */}
        <div className="mb-4 p-3 bg-slate-900/50 rounded-lg border border-slate-700/50">
          <h4 className="text-sm font-medium text-cyan-300 mb-2">Performance Metrics</h4>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="text-cyan-400/60">
              Avg Gen: {performanceMetrics.averageGenerationTime > 0 ? `${performanceMetrics.averageGenerationTime.toFixed(1)}ms` : 'N/A'}
            </div>
            <div className="text-cyan-400/60">
              Last Gen: {performanceMetrics.lastGenerationTime > 0 ? `${performanceMetrics.lastGenerationTime.toFixed(1)}ms` : 'N/A'}
            </div>
            <div className="text-cyan-400/60">
              Total Gen: {performanceMetrics.totalGenerations}
            </div>
            <div className="text-cyan-400/60">
              Sample Rate: {audioContextRef.current?.sampleRate || 'N/A'}Hz
            </div>
          </div>
        </div>

        {/* Status */}
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-4">
            <div className={`flex items-center space-x-2 ${currentBuffer ? 'text-green-400' : 'text-gray-500'}`}>
              <div className={`w-2 h-2 rounded-full ${currentBuffer ? 'bg-green-400' : 'bg-gray-500'}`}></div>
              <span>Buffer: {currentBuffer ? 'Ready' : 'Empty'}</span>
            </div>
            <div className="text-cyan-400/60">
              Algorithm: {algorithms.find(a => a.id === parameters.algorithm)?.name}
            </div>
            <div className="text-purple-400/60">
              Attention: {parameters.attention_heads} heads
            </div>
            <div className="text-green-400/60">
              Spatial: {parameters.distance.toFixed(1)}u
            </div>
          </div>
          <div className="text-cyan-400/40">
            SWS Neural Engine v3.0 - Quantum AI
          </div>
        </div>
      </div>
    </div>
  )
})
SWSNeuralSynthesizer.displayName = 'SWSNeuralSynthesizer'

export default SWSNeuralSynthesizer;
